import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Trash2, FileText, CheckCircle2, Clock, XCircle, Camera, CreditCard, ExternalLink } from 'lucide-react';
import api, { fileToPayload, imageToPayload, fileUrl } from './api';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';
import { UKDatePicker, fmtUK } from './UKDate';

const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';

const StatusBanner = ({ status, reason, suspended }) => {
  if (suspended) return null;
  const map = {
    verified: { icon: CheckCircle2, cls: 'bg-green-50 text-green-800', text: 'Your profile is verified. You can browse and bid on jobs.' },
    pending: { icon: Clock, cls: 'bg-amber-50 text-amber-800', text: 'Your profile is under review by EA Lead Solutions. You will be notified once approved.' },
    rejected: { icon: XCircle, cls: 'bg-red-50 text-red-800', text: `Your submission needs attention: ${reason || 'Please review your documents and resubmit.'}` },
    incomplete: { icon: Clock, cls: 'bg-blue-50 text-blue-800', text: 'Complete your profile and upload your documents, then submit for verification to start bidding.' },
  };
  const s = map[status] || map.incomplete;
  const Icon = s.icon;
  return (
    <div data-testid="verification-status-banner" className={`flex items-start gap-3 rounded-2xl p-4 text-sm shadow-sm ${s.cls}`}>
      <Icon size={18} className="mt-0.5 shrink-0" /><span>{s.text}</span>
    </div>
  );
};

const OperativeProfile = () => {
  const { user, loading, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const photoRef = useRef();
  const docRef = useRef();
  const [profile, setProfile] = useState(null);
  const [cats, setCats] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [docType, setDocType] = useState('');
  const [docExpiry, setDocExpiry] = useState('');
  const [stripe, setStripe] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const MANDATORY = ['Public Liability Insurance', 'Gas Safe Registration', 'NICEIC / Electrical Certification', 'DBS Check'];
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ bio: '', phone: '', whatsapp: '', companyName: '', yearsExperience: '', hourlyRate: '', categories: [], serviceAreas: '', skills: '' });
  const [feeTier, setFeeTier] = useState(null);
  useEffect(() => { if (user && user.role === 'operative') api.get('/my/fee-tier').then((r) => setFeeTier(r.data)).catch(() => {}); }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/auth?role=operative&next=/profile/operative'); return; }
    if (user.role !== 'operative') { navigate('/dashboard/client'); return; }
    api.get('/marketplace/config').then((r) => { setCats(r.data.categories); setDocTypes(r.data.documentTypes); setDocType(r.data.documentTypes[0]); });
    load();
    loadStripe();
    if (params.get('stripe')) { refresh(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const loadStripe = async () => {
    try { const r = await api.get('/stripe/connect/status'); setStripe(r.data); if (r.data.chargesEnabled) refresh(); }
    catch (e) { setStripe({ connected: !!user?.stripeConnected, chargesEnabled: !!user?.stripeChargesEnabled, payoutsEnabled: !!user?.stripePayoutsEnabled }); }
  };
  const connectStripe = async () => {
    setConnecting(true);
    try { const r = await api.post('/stripe/connect/onboard', { origin: window.location.origin }); window.location.href = r.data.url; }
    catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not start Stripe onboarding.', variant: 'destructive' }); setConnecting(false); }
  };

  const load = async () => {
    const r = await api.get('/profile/me');
    setProfile(r.data);
    setForm({
      bio: r.data.bio || '', phone: r.data.phone || '', whatsapp: r.data.whatsapp || '', companyName: r.data.companyName || '',
      yearsExperience: r.data.yearsExperience || '', hourlyRate: r.data.hourlyRate ?? '',
      categories: r.data.categories || [], serviceAreas: (r.data.serviceAreas || []).join(', '),
      skills: (r.data.skills || []).join(', '),
    });
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        bio: form.bio, phone: form.phone, whatsapp: form.whatsapp, companyName: form.companyName,
        yearsExperience: form.yearsExperience,
        hourlyRate: form.hourlyRate === '' ? null : parseFloat(form.hourlyRate),
        categories: form.categories,
        serviceAreas: form.serviceAreas.split(',').map((s) => s.trim()).filter(Boolean),
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const r = await api.put('/profile', payload);
      setProfile(r.data);
      toast({ title: 'Profile saved' });
    } catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not save.', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const p = await imageToPayload(file); await api.post('/profile/photo', p); await load(); toast({ title: 'Photo updated' }); }
    catch (err) { toast({ title: 'Error', description: err?.response?.data?.detail || err?.message || 'Upload failed. Please try a different image.', variant: 'destructive' }); }
    finally { setUploading(false); if (photoRef.current) photoRef.current.value = ''; }
  };

  const onDoc = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (MANDATORY.includes(docType) && !docExpiry) { toast({ title: 'Expiry date required', description: `Please set an expiry date for ${docType}.`, variant: 'destructive' }); if (docRef.current) docRef.current.value = ''; return; }
    if (file.size > 25 * 1024 * 1024) { toast({ title: 'File too large', description: 'Documents must be under 25MB.', variant: 'destructive' }); if (docRef.current) docRef.current.value = ''; return; }
    setUploading(true);
    try { const p = await fileToPayload(file); await api.post('/profile/documents', { docType, expiryDate: docExpiry, ...p }); setDocExpiry(''); await load(); toast({ title: 'Document uploaded' }); }
    catch (err) { toast({ title: 'Error', description: err?.response?.data?.detail || 'Upload failed.', variant: 'destructive' }); }
    finally { setUploading(false); if (docRef.current) docRef.current.value = ''; }
  };

  const delDoc = async (id) => { try { await api.delete(`/profile/documents/${id}`); await load(); toast({ title: 'Document removed' }); } catch (e) { toast({ title: 'Error', description: 'Could not remove document.', variant: 'destructive' }); } };

  const submit = async () => {
    setSubmitting(true);
    try { await api.post('/profile/submit'); await load(); await refresh(); toast({ title: 'Submitted for verification', description: 'Our team will review your profile shortly.' }); }
    catch (e) { toast({ title: 'Not ready yet', description: e?.response?.data?.detail || 'Please complete your profile.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  if (!profile) return <div className="py-24 text-center text-gray-500">Loading profile…</div>;
  const status = profile.verificationStatus;

  return (
    <div className="bg-[#e9f3ec] py-12 min-h-[70vh]" data-testid="operative-profile-page">
      <div className="mx-auto max-w-3xl px-5">
        <h1 className="ea-heading text-2xl font-bold">My Professional Profile</h1>
        <p className="mt-1 text-sm text-gray-500">Complete your profile and submit your documents to get verified.</p>

        <div className="mt-5"><StatusBanner status={status} reason={profile.rejectionReason} suspended={profile.suspended} /></div>
        {profile.suspended && <div className="mt-3 flex items-start gap-3 rounded-2xl bg-red-50 p-4 text-sm text-red-800 shadow-sm" data-testid="suspended-banner"><span>⚠️ Your account is suspended from bidding: {profile.suspendedReason || 'a required document has expired'}. Upload a valid renewed document — an admin will restore your access.</span></div>}

        {/* Stripe Connect — required before bidding */}
        {status === 'verified' && (
          <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm ring-2 ring-[#1b4f72]/15" data-testid="stripe-connect-card">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#e9f3ec] p-2 text-[#1b4f72]"><CreditCard size={20} /></div>
              <div className="flex-1">
                <h2 className="font-display text-lg font-bold text-[#1b4f72]">Payout Account (Stripe) <span className="text-red-500">*</span></h2>
                <p className="mt-1 text-sm text-gray-500">You must connect a Stripe payout account before you can bid. Payouts run weekly (Mondays).</p>
                {feeTier && (
                  <div data-testid="operative-fee-tier" className="mt-3 rounded-xl border border-[#3a9b5c]/30 bg-[#e9f3ec] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-[#1b4f72]">Your fee tier: <span data-testid="fee-tier-number">Tier {feeTier.tierNumber}</span> — you keep <span data-testid="fee-tier-keep">{feeTier.operativeKeepPct}%</span> of each final payment</span>
                      {feeTier.manual && <span data-testid="fee-tier-manual" className="rounded-full bg-[#b8860b] px-2.5 py-0.5 text-xs font-semibold text-white">Manually set by admin</span>}
                    </div>
                    <p className="mt-1 text-xs text-gray-600">You also receive {100 - feeTier.depositPlatformPct}% of the 15% deposit as site-visit compensation. Lifetime released earnings: £{(feeTier.lifetimeEarnings || 0).toFixed(2)}.{!feeTier.manual && feeTier.next ? ` Earn £${feeTier.next.remaining.toFixed(2)} more to reach ${feeTier.next.nextKeepPct}% on future jobs.` : ''}</p>
                  </div>
                )}
                {stripe && stripe.chargesEnabled && stripe.payoutsEnabled ? (
                  <p className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2.5 text-sm font-medium text-green-800" data-testid="stripe-connected"><CheckCircle2 size={16} /> Payout account connected and active. You can bid on jobs.</p>
                ) : stripe && stripe.connected ? (
                  <div className="mt-3">
                    <p className="mb-2 rounded-lg bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800" data-testid="stripe-incomplete">Your Stripe onboarding is incomplete. Finish it to enable bidding.</p>
                    <button data-testid="connect-stripe-btn" disabled={connecting} onClick={connectStripe} className="flex items-center gap-2 rounded-full bg-[#635bff] px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"><ExternalLink size={16} /> {connecting ? 'Redirecting…' : 'Continue Stripe Onboarding'}</button>
                  </div>
                ) : (
                  <button data-testid="connect-stripe-btn" disabled={connecting} onClick={connectStripe} className="mt-3 flex items-center gap-2 rounded-full bg-[#635bff] px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"><CreditCard size={16} /> {connecting ? 'Redirecting…' : 'Connect Payout Account'}</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents — shown first so new operatives see it immediately */}
        <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm ring-2 ring-[#3a9b5c]/20">
          <h2 className="font-display text-lg font-bold text-[#1b4f72]">Certifications &amp; Documents <span className="text-red-500">*</span></h2>
          <p className="mt-1 text-sm text-gray-500">Upload your certificates, insurance and ID. At least one document is required before you can be verified.</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select data-testid="doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm">
              {docTypes.map((d) => <option key={d}>{d}</option>)}
            </select>
            <div className="flex flex-col">
              <label className="text-[11px] text-gray-400">Expiry date{MANDATORY.includes(docType) ? ' *' : ' (optional)'}</label>
              <div className="w-44"><UKDatePicker testId="doc-expiry-input" value={docExpiry} onChange={setDocExpiry} /></div>
            </div>
            <button data-testid="upload-doc-btn" disabled={uploading} onClick={() => docRef.current.click()} className="flex items-center gap-2 rounded-full bg-[#3a9b5c] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#318150] disabled:opacity-60"><Upload size={16} /> {uploading ? 'Uploading…' : 'Upload Document'}</button>
            <input ref={docRef} type="file" accept=".pdf,image/*,.doc,.docx" className="hidden" onChange={onDoc} />
          </div>
          <div className="mt-4 space-y-2" data-testid="documents-list">
            {profile.documents.length === 0 ? <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-400">No documents uploaded yet.</p> : profile.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                <a href={fileUrl(d.id, { auth: true })} target="_blank" rel="noreferrer" className="flex flex-wrap items-center gap-2 text-sm text-[#1b4f72] hover:underline"><FileText size={16} className="text-[#3a9b5c]" /><span className="font-medium">{d.docType}</span><span className="text-gray-400">· {d.filename}</span>{d.expiryDate && <span className="text-xs text-gray-400">· expires {fmtUK(d.expiryDate)}</span>}</a>
                <button data-testid={`del-doc-${d.id}`} onClick={() => delDoc(d.id)} className="text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Photo */}
        <div className="mt-6 flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm">
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full bg-[#e9f3ec] ring-2 ring-[#3a9b5c]/30">
            {profile.photoFileId
              ? <img src={fileUrl(profile.photoFileId)} alt="Profile" className="h-full w-full object-cover" />
              : <div className="flex h-full w-full items-center justify-center text-[#3a9b5c]"><Camera size={26} /></div>}
          </div>
          <div>
            <p className="font-semibold text-[#1b4f72]">{profile.name}</p>
            <button data-testid="upload-photo-btn" onClick={() => photoRef.current.click()} className="mt-1 text-sm font-medium text-[#3a9b5c] hover:underline">Upload profile photo</button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
          </div>
        </div>

        {/* Profile fields */}
        <div className="mt-5 space-y-4 rounded-2xl bg-white p-6 shadow-sm">
          <div><label className={labelCls}>About you / Bio *</label><textarea data-testid="profile-bio" rows={4} className={inputCls} value={form.bio} onChange={(e) => upd('bio', e.target.value)} placeholder="Describe your experience, specialities and what makes you reliable (min 30 characters)." /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className={labelCls}>Contact Phone *</label><input data-testid="profile-phone" className={inputCls} value={form.phone} onChange={(e) => upd('phone', e.target.value)} /></div>
            <div><label className={labelCls}>WhatsApp Number</label><input data-testid="profile-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e) => upd('whatsapp', e.target.value)} placeholder="e.g. +44 7123 456789" /></div>
            <div><label className={labelCls}>Company Name (optional)</label><input data-testid="profile-company" className={inputCls} value={form.companyName} onChange={(e) => upd('companyName', e.target.value)} /></div>
            <div><label className={labelCls}>Years of Experience</label><input data-testid="profile-experience" className={inputCls} value={form.yearsExperience} onChange={(e) => upd('yearsExperience', e.target.value)} placeholder="e.g. 8" /></div>
            <div><label className={labelCls}>Hourly Rate (£, optional)</label><input data-testid="profile-rate" type="number" className={inputCls} value={form.hourlyRate} onChange={(e) => upd('hourlyRate', e.target.value)} placeholder="e.g. 35" /></div>
          </div>
          <div>
            <label className={labelCls}>Service Categories *</label>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button type="button" key={c} data-testid={`cat-${c}`} onClick={() => toggleCat(c)} className={`rounded-full border px-3.5 py-1.5 text-sm ${form.categories.includes(c) ? 'border-[#3a9b5c] bg-[#e9f3ec] font-semibold text-[#1b4f72]' : 'border-gray-200 text-gray-600'}`}>{c}</button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Service Areas *</label><input data-testid="profile-areas" className={inputCls} value={form.serviceAreas} onChange={(e) => upd('serviceAreas', e.target.value)} placeholder="Comma-separated, e.g. Camden, Islington, SW1, North London" /></div>
          <div><label className={labelCls}>Key Skills</label><input data-testid="profile-skills" className={inputCls} value={form.skills} onChange={(e) => upd('skills', e.target.value)} placeholder="Comma-separated, e.g. Boiler repair, Rewiring, Emergency callouts" /></div>
          <button data-testid="save-profile-btn" disabled={saving} onClick={save} className="rounded-full bg-[#1b4f72] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#163f5c] disabled:opacity-60">{saving ? 'Saving…' : 'Save Profile'}</button>
        </div>

        {/* Submit */}
        {status !== 'verified' && (
          <div className="mt-5 rounded-2xl bg-white p-6 text-center shadow-sm">
            <p className="text-sm text-gray-600">{status === 'pending' ? 'Your profile is awaiting review. You can still update it and it will be re-checked.' : 'Once your profile and documents are ready, submit for verification.'}</p>
            <button data-testid="submit-verification-btn" disabled={submitting} onClick={submit} className="mt-3 rounded-full bg-[#3a9b5c] px-8 py-3 font-semibold text-white hover:bg-[#318150] disabled:opacity-60">{submitting ? 'Submitting…' : (status === 'pending' ? 'Re-submit for Verification' : 'Submit for Verification')}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperativeProfile;
